import React, { useEffect, useMemo, useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';
import { scheduleAPI } from '../services/api.ts';

export interface ServiceReportField {
  key: string;
  label: string;
  input: 'text' | 'number' | 'select' | 'multi' | 'perpest' | string;
  unit?: string | null;
  of?: string | null;
  area?: boolean | null;
  options?: string[];
}

export interface ServiceReportSection {
  key: string;
  label: string;
  type: 'checklist' | 'notes' | 'fields' | 'table' | string;
  addLabel?: string | null;
  items?: string[] | null;
  lines?: string[] | null;
  fields?: ServiceReportField[] | null;
  columns?: ServiceReportField[] | null;
}

export interface ServiceCompletionDetail {
  id: number;
  assignmentId?: number | null;
  sitePicId?: number | null;
  documentNo: string;
  customerName: string;
  serviceName: string;
  scheduleDate?: string;
  checkedInDateTime?: string | null;
  isCompleted: boolean;
  completion?: Record<string, any> | null;
  afterPhotoCount: number;
  signatureDataUrl?: string | null;
  serviceReportConfig?: { sections?: ServiceReportSection[] };
}

interface Props {
  detail: ServiceCompletionDetail;
  onClose: () => void;
  onSubmitted: () => void | Promise<void>;
  onPrint: () => void;
}

type Answers = Record<string, any>;

const isOptionField = (field: ServiceReportField) =>
  field.input === 'select' || field.input === 'multi' || field.input === 'perpest';

const usableFields = (fields?: ServiceReportField[] | null) =>
  (fields || []).filter((field) =>
    !!field.key &&
    !!field.label &&
    (!isOptionField(field) || (field.options || []).length > 0),
  );

const usableSections = (sections?: ServiceReportSection[]) =>
  (sections || []).filter((section) => {
    if (!section.key || !section.label) return false;
    if (section.type === 'checklist') return (section.items || []).length > 0;
    if (section.type === 'notes') return (section.lines || []).length > 0;
    if (section.type === 'fields') return usableFields(section.fields).length > 0;
    if (section.type === 'table') return usableFields(section.columns).length > 0;
    return false;
  });

const initialAnswers = (sections: ServiceReportSection[]): Answers => {
  const result: Answers = {};
  sections.forEach((section) => {
    if (section.type === 'checklist') {
      result[section.key] = (section.items || []).map((item) => ({ item, checked: true }));
    } else if (section.type === 'fields') {
      result[section.key] = {};
    } else if (section.type === 'table') {
      result[section.key] = [{}];
    }
  });
  return result;
};

const parseSavedReport = (detail: ServiceCompletionDetail): Answers | null => {
  const raw = detail.completion?.serviceReportJson ?? detail.completion?.serviceReport;
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    return parsed?.sections && typeof parsed.sections === 'object' ? parsed.sections : null;
  } catch {
    return null;
  }
};

const valuePresent = (value: any): boolean => {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return value !== undefined && value !== null && String(value).trim() !== '';
};

const displayValue = (value: any): string => {
  if (Array.isArray(value)) {
    return value.map((item) => typeof item === 'object' ? item.item || item.name || '' : item)
      .filter(Boolean)
      .join(', ');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([key, item]: [string, any]) => {
      const level = typeof item === 'object' ? item.lv : item;
      const area = typeof item === 'object' ? item.area : '';
      return `${key}: ${level || 'Selected'}${area ? ` · ${area}` : ''}`;
    }).join('; ');
  }
  return String(value ?? '');
};

const ServiceCompletionSheet: React.FC<Props> = ({ detail, onClose, onSubmitted, onPrint }) => {
  const sections = useMemo(
    () => usableSections(detail.serviceReportConfig?.sections),
    [detail.serviceReportConfig?.sections],
  );
  const savedAnswers = useMemo(() => parseSavedReport(detail), [detail]);
  const [stage, setStage] = useState<'edit' | 'confirm' | 'completed'>(
    detail.isCompleted ? 'completed' : 'edit',
  );
  const [answers, setAnswers] = useState<Answers>(() =>
    savedAnswers || initialAnswers(sections),
  );
  const [notes, setNotes] = useState(String(detail.completion?.remarks || ''));
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showUnavailable, setShowUnavailable] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState('Customer not on site');
  const signatureRef = useRef<SignatureCanvas | null>(null);

  useEffect(() => {
    if (!navigator.geolocation || detail.isCompleted) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLatitude(position.coords.latitude);
        setLongitude(position.coords.longitude);
      },
      () => undefined,
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, [detail.isCompleted]);

  const updateSection = (sectionKey: string, next: any) =>
    setAnswers((current) => ({ ...current, [sectionKey]: next }));

  const updateField = (sectionKey: string, fieldKey: string, value: any) =>
    setAnswers((current) => ({
      ...current,
      [sectionKey]: { ...(current[sectionKey] || {}), [fieldKey]: value },
    }));

  const toggleMulti = (sectionKey: string, fieldKey: string, option: string) => {
    const values = [...(answers[sectionKey]?.[fieldKey] || [])];
    const index = values.indexOf(option);
    if (index >= 0) values.splice(index, 1);
    else values.push(option);
    updateField(sectionKey, fieldKey, values);
  };

  const gradeLinkedItem = (
    sectionKey: string,
    sourceKey: string,
    gradeKey: string,
    item: string,
    grade: string,
  ) => {
    const sectionValue = { ...(answers[sectionKey] || {}) };
    const selected = [...(sectionValue[sourceKey] || [])];
    const grades = { ...(sectionValue[gradeKey] || {}) };
    const current = grades[item];
    const currentLevel = typeof current === 'object' ? current.lv : current;
    if (currentLevel === grade && selected.includes(item)) {
      delete grades[item];
      sectionValue[sourceKey] = selected.filter((value) => value !== item);
    } else {
      if (!selected.includes(item)) selected.push(item);
      grades[item] = { lv: grade, area: typeof current === 'object' ? current.area || '' : '' };
      sectionValue[sourceKey] = selected;
    }
    sectionValue[gradeKey] = grades;
    updateSection(sectionKey, sectionValue);
  };

  const updateGradeArea = (sectionKey: string, fieldKey: string, item: string, area: string) => {
    const values = { ...(answers[sectionKey]?.[fieldKey] || {}) };
    const current = values[item];
    values[item] = {
      lv: typeof current === 'object' ? current.lv || '' : current || '',
      area,
    };
    updateField(sectionKey, fieldKey, values);
  };

  const updateTableCell = (sectionKey: string, rowIndex: number, fieldKey: string, value: any) => {
    const rows = [...(answers[sectionKey] || [{}])];
    rows[rowIndex] = { ...(rows[rowIndex] || {}), [fieldKey]: value };
    updateSection(sectionKey, rows);
  };

  const submit = async (customerUnavailableReason = '') => {
    if (submitting) return;
    let signatureDataUrl = '';
    if (!customerUnavailableReason) {
      if (!signatureRef.current || signatureRef.current.isEmpty()) {
        toast.error('Ask the customer to sign, or choose Customer not available');
        return;
      }
      signatureDataUrl = signatureRef.current.getCanvas().toDataURL('image/png');
    }
    setSubmitting(true);
    try {
      await scheduleAPI.completeService(detail.id, detail.assignmentId, detail.sitePicId, {
        notes: notes.trim(),
        latitude,
        longitude,
        signatureDataUrl,
        customerUnavailableReason,
        serviceReport: {
          service: detail.serviceName,
          sections: answers,
        },
      });
      await onSubmitted();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Unable to submit the service report');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (
    section: ServiceReportSection,
    field: ServiceReportField,
    linkedFields: Record<string, ServiceReportField>,
  ) => {
    const value = answers[section.key]?.[field.key];
    if (field.input === 'perpest' && field.of && linkedFields[field.of]) return null;
    if (field.input === 'multi' && linkedFields[field.key]) {
      const gradeField = linkedFields[field.key];
      const selected: string[] = answers[section.key]?.[field.key] || [];
      const grades = answers[section.key]?.[gradeField.key] || {};
      return (
        <div className="service-completion-field" key={field.key}>
          <div className="service-completion-field-label">
            {field.label}<small>Tap one grade for each applicable item</small>
          </div>
          <div className="service-completion-grade-list">
            {(field.options || []).map((option) => {
              const grade = grades[option];
              const level = typeof grade === 'object' ? grade.lv : grade;
              const isSelected = selected.includes(option);
              return (
                <div className={`service-completion-grade-row ${isSelected ? 'selected' : ''}`} key={option}>
                  <strong>{option}</strong>
                  <div>
                    {(gradeField.options || []).map((choice) => (
                      <button
                        type="button"
                        className={level === choice && isSelected ? 'active' : ''}
                        onClick={() => gradeLinkedItem(section.key, field.key, gradeField.key, option, choice)}
                        key={choice}
                      >
                        {choice}
                      </button>
                    ))}
                  </div>
                  {isSelected && gradeField.area !== false ? (
                    <input
                      value={typeof grade === 'object' ? grade.area || '' : ''}
                      onChange={(event) => updateGradeArea(section.key, gradeField.key, option, event.target.value)}
                      placeholder="Area / location"
                    />
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    if (field.input === 'select' || field.input === 'multi') {
      const isMulti = field.input === 'multi';
      return (
        <fieldset className="service-completion-field" key={field.key}>
          <legend className="service-completion-field-label">
            {field.label}<small>{isMulti ? 'Tick all that apply' : 'Select one'}</small>
          </legend>
          <div className="service-completion-options">
            {(field.options || []).map((option) => {
              const checked = isMulti ? (value || []).includes(option) : value === option;
              return (
                <label className={checked ? 'selected' : ''} key={option}>
                  <input
                    type={isMulti ? 'checkbox' : 'radio'}
                    name={`${section.key}-${field.key}`}
                    checked={checked}
                    onChange={() => isMulti
                      ? toggleMulti(section.key, field.key, option)
                      : updateField(section.key, field.key, option)}
                  />
                  <span>{option}</span>
                </label>
              );
            })}
          </div>
        </fieldset>
      );
    }
    return (
      <label className="service-completion-field" key={field.key}>
        <span className="service-completion-field-label">
          {field.label}{field.unit ? <small>{field.unit}</small> : null}
        </span>
        <input
          type={field.input === 'number' ? 'number' : 'text'}
          inputMode={field.input === 'number' ? 'decimal' : undefined}
          value={value || ''}
          placeholder={field.unit || ''}
          onChange={(event) => updateField(section.key, field.key, event.target.value)}
        />
      </label>
    );
  };

  const renderSection = (section: ServiceReportSection) => {
    if (section.type === 'notes') {
      return (
        <section className="service-completion-notice" key={section.key}>
          <strong>{section.label}</strong>
          {(section.lines || []).map((line) => <p key={line}>• {line}</p>)}
        </section>
      );
    }
    if (section.type === 'checklist') {
      const values = answers[section.key] || [];
      return (
        <section className="service-completion-section" key={section.key}>
          <h3>{section.label}</h3>
          <div className="service-completion-checklist">
            {(section.items || []).map((item, index) => (
              <label key={item}>
                <input
                  type="checkbox"
                  checked={values[index]?.checked !== false}
                  onChange={(event) => {
                    const next = [...values];
                    next[index] = { item, checked: event.target.checked };
                    updateSection(section.key, next);
                  }}
                />
                <span>{item}</span>
              </label>
            ))}
          </div>
          <small className="service-completion-help">Untick anything not completed or failed.</small>
        </section>
      );
    }
    if (section.type === 'fields') {
      const fields = usableFields(section.fields);
      const linkedFields: Record<string, ServiceReportField> = {};
      fields.forEach((field) => {
        if (field.input === 'perpest' && field.of) linkedFields[field.of] = field;
      });
      return (
        <section className="service-completion-section" key={section.key}>
          <h3>{section.label}</h3>
          {fields.map((field) => renderField(section, field, linkedFields))}
        </section>
      );
    }
    const columns = usableFields(section.columns);
    const rows = answers[section.key] || [{}];
    return (
      <section className="service-completion-section" key={section.key}>
        <h3>{section.label}</h3>
        {rows.map((row: Record<string, any>, rowIndex: number) => (
          <div className="service-completion-table-row" key={rowIndex}>
            <div className="service-completion-row-head">
              <strong>#{rowIndex + 1}</strong>
              {rows.length > 1 ? (
                <button type="button" onClick={() => updateSection(section.key, rows.filter((_: any, index: number) => index !== rowIndex))}>
                  Remove
                </button>
              ) : null}
            </div>
            <div className="service-completion-table-grid">
              {columns.map((column) => (
                <label key={column.key}>
                  <span>{column.label}{column.unit ? ` (${column.unit})` : ''}</span>
                  {column.input === 'select' ? (
                    <select
                      value={row[column.key] || ''}
                      onChange={(event) => updateTableCell(section.key, rowIndex, column.key, event.target.value)}
                    >
                      <option value="">Select</option>
                      {(column.options || []).map((option) => <option value={option} key={option}>{option}</option>)}
                    </select>
                  ) : column.input === 'multi' ? (
                    <div className="service-completion-table-options">
                      {(column.options || []).map((option) => {
                        const selected = (row[column.key] || []).includes(option);
                        return (
                          <label className={selected ? 'selected' : ''} key={option}>
                            <input
                              type="checkbox"
                              checked={selected}
                              onChange={() => {
                                const values = [...(row[column.key] || [])];
                                const optionIndex = values.indexOf(option);
                                if (optionIndex >= 0) values.splice(optionIndex, 1);
                                else values.push(option);
                                updateTableCell(section.key, rowIndex, column.key, values);
                              }}
                            />
                            {option}
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      type={column.input === 'number' ? 'number' : 'text'}
                      inputMode={column.input === 'number' ? 'decimal' : undefined}
                      value={row[column.key] || ''}
                      onChange={(event) => updateTableCell(section.key, rowIndex, column.key, event.target.value)}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>
        ))}
        <button
          type="button"
          className="service-completion-add-row"
          onClick={() => updateSection(section.key, [...rows, {}])}
        >
          + {section.addLabel || 'Add item'}
        </button>
      </section>
    );
  };

  const reportSummary = () => sections.map((section) => {
    if (section.type === 'notes') return null;
    const value = answers[section.key];
    if (section.type === 'checklist') {
      const checked = (value || []).filter((item: any) => item.checked);
      if (!checked.length) return null;
      return (
        <section className="service-confirm-section" key={section.key}>
          <h3>{section.label}</h3>
          {checked.map((item: any) => <p key={item.item}>✓ {item.item}</p>)}
        </section>
      );
    }
    if (section.type === 'table') {
      const rows = (value || []).filter((row: any) => Object.values(row).some(valuePresent));
      if (!rows.length) return null;
      return (
        <section className="service-confirm-section" key={section.key}>
          <h3>{section.label}</h3>
          {rows.map((row: any, index: number) => (
            <p key={index}>{Object.values(row).filter(valuePresent).map(displayValue).join(' · ')}</p>
          ))}
        </section>
      );
    }
    const visible = Object.entries(value || {}).filter(([, fieldValue]) => valuePresent(fieldValue));
    if (!visible.length) return null;
    const fields = usableFields(section.fields);
    return (
      <section className="service-confirm-section" key={section.key}>
        <h3>{section.label}</h3>
        {visible.map(([key, fieldValue]) => (
          <div className="service-confirm-answer" key={key}>
            <span>{fields.find((field) => field.key === key)?.label || key}</span>
            <strong>{displayValue(fieldValue)}</strong>
          </div>
        ))}
      </section>
    );
  });

  return (
    <>
      <div className="schedule-sheet-bg" onClick={onClose} />
      <section className="schedule-sheet service-completion-sheet" role="dialog" aria-modal="true">
        <div className="schedule-sheet-handle" />
        <header className="schedule-sheet-header">
          <div className="schedule-sheet-title">
            {stage === 'confirm' ? 'Customer Confirmation' : stage === 'completed' ? `Report · ${detail.customerName}` : `Completion Report: ${detail.documentNo}`}
          </div>
          <button type="button" className="schedule-sheet-close" onClick={onClose} aria-label="Close">×</button>
        </header>

        <div className="schedule-sheet-body service-completion-body">
          {stage === 'edit' ? (
            <>
              <div className="delivery-completion-summary">
                <strong>{detail.customerName} — {detail.serviceName}</strong>
                <span>{detail.documentNo}</span>
              </div>
              {sections.length ? sections.map(renderSection) : (
                <div className="service-completion-empty">
                  No Service Report setup is published for this business in Catalog Management.
                </div>
              )}
              <label className="service-completion-field service-completion-notes">
                <span className="service-completion-field-label">Completion notes</span>
                <textarea
                  rows={4}
                  value={notes}
                  placeholder="Describe work completed…"
                  onChange={(event) => setNotes(event.target.value)}
                />
              </label>
              <div className="delivery-completion-time">
                <div>
                  <span>Start</span>
                  <strong>{detail.checkedInDateTime ? new Date(detail.checkedInDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '—'}</strong>
                </div>
                <div><span>End</span><strong>on submit</strong></div>
                <small>taken from your job punch — no typing</small>
              </div>
              {detail.afterPhotoCount > 0 ? (
                <div className="service-completion-photo-proof">📷 {detail.afterPhotoCount} after photo{detail.afterPhotoCount === 1 ? '' : 's'} included</div>
              ) : null}
              <button type="button" className="delivery-completion-primary" onClick={() => setStage('confirm')}>
                Review with customer ›
              </button>
            </>
          ) : (
            <>
              {stage === 'confirm' ? (
                <>
                  <div className="delivery-confirm-handoff">📱 Please hand the device to the customer</div>
                  <button type="button" className="delivery-confirm-back" onClick={() => setStage('edit')}>‹ Back to edit</button>
                </>
              ) : (
                <div className="service-completion-success">
                  <strong>✓ Service completed</strong>
                  <span>Report submitted{detail.completion?.checkoutTime ? ` · ${detail.completion.checkoutTime}` : ''}</span>
                </div>
              )}
              <div className="delivery-completion-summary">
                <strong>{detail.customerName}</strong>
                <span>{detail.serviceName} · {detail.documentNo}</span>
              </div>
              {reportSummary()}
              {notes ? (
                <section className="service-confirm-section">
                  <h3>Work completed</h3>
                  <p>{notes}</p>
                </section>
              ) : null}
              <section className="service-confirm-section">
                <h3>Time</h3>
                <p>Started {detail.checkedInDateTime ? new Date(detail.checkedInDateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }) : '—'} · {stage === 'completed' ? 'completed' : 'ends on submit'}</p>
              </section>
              <div className="delivery-confirm-statement">I confirm the work listed above has been completed.</div>

              {stage === 'confirm' ? (
                <>
                  <div className="delivery-completion-label">Customer signature</div>
                  <div className="delivery-signature-wrap">
                    <SignatureCanvas ref={signatureRef} penColor="#111827" canvasProps={{ className: 'delivery-signature-canvas' }} />
                    <span>Sign here</span>
                  </div>
                  <button type="button" className="delivery-signature-clear" onClick={() => signatureRef.current?.clear()}>Clear</button>
                  <button
                    type="button"
                    className="delivery-completion-primary"
                    disabled={submitting}
                    onClick={() => void submit()}
                  >
                    {submitting ? 'Submitting…' : 'Confirm & submit'}
                  </button>
                  <button type="button" className="delivery-confirm-skip" onClick={() => setShowUnavailable((value) => !value)}>
                    Customer not available ›
                  </button>
                  {showUnavailable ? (
                    <div className="service-completion-unavailable">
                      <label>
                        <span>Why is there no signature?</span>
                        <select value={unavailableReason} onChange={(event) => setUnavailableReason(event.target.value)}>
                          <option>Customer not on site</option>
                          <option>Customer refused to sign</option>
                          <option>Other</option>
                        </select>
                      </label>
                      <button type="button" disabled={submitting} onClick={() => void submit(unavailableReason)}>
                        Submit without signature
                      </button>
                    </div>
                  ) : null}
                </>
              ) : detail.signatureDataUrl ? (
                <section className="service-confirm-section">
                  <h3>Signature</h3>
                  <img className="service-completion-saved-signature" src={detail.signatureDataUrl} alt="Customer signature" />
                </section>
              ) : detail.completion?.customerUnavailableReason ? (
                <section className="service-confirm-section">
                  <h3>No signature</h3>
                  <p>{detail.completion.customerUnavailableReason}</p>
                </section>
              ) : null}
            </>
          )}
        </div>
        {stage === 'completed' ? (
          <footer className="service-completion-print-footer">
            <button type="button" onClick={onPrint}>
              <span aria-hidden="true">🖨️</span>
              Print service report
            </button>
          </footer>
        ) : null}
      </section>
    </>
  );
};

export default ServiceCompletionSheet;
